/**
 * Invoice mutation hooks using generic factory
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { invoiceApi } from '@/lib/api/invoices.api';
import { queryKeys } from '../query-keys';
import { createResourceMutations } from './createResourceMutations';
import { toast } from 'sonner';
import { useInvalidateClientBillingRun } from '../queries/useClientBillingRunQuery';
import type {
  CreateBillingRunPayload,
  CreateInvoice,
  Invoice,
  UpdateInvoiceInput,
} from '@scrambl/shared';

const mutations = createResourceMutations<Invoice, CreateInvoice, UpdateInvoiceInput>({
  resourceName: 'Invoice',
  resourceNameSingular: 'invoice',
  queryKeyFactory: queryKeys.invoices,
  api: {
    create: (data: CreateInvoice) => invoiceApi.createInvoice(data),
    update: (id: string, data: UpdateInvoiceInput) => invoiceApi.updateInvoice(id, data),
    updateField: (id: string, field: string, value: unknown) => {
      const updateData = { [field]: value } as UpdateInvoiceInput;
      return invoiceApi.updateInvoice(id, updateData);
    },
    delete: (id: string) => invoiceApi.deleteInvoice(id),
  },
  enableRealtime: false,
});

export const useCreateInvoiceMutation = mutations.useCreateMutation;
export const useUpdateInvoiceMutation = mutations.useUpdateMutation;
export const useUpdateInvoiceFieldMutation = mutations.useUpdateFieldMutation!;
export const useDeleteInvoiceMutation = mutations.useDeleteMutation;

/**
 * Client billing run — creates one invoice for the client, or one per project,
 * in a single action. The whole run is transactional server-side: either every
 * invoice exists and every hour is claimed, or none of it happened.
 */
export function useCreateClientBillingRunMutation(entityId: string) {
  const queryClient = useQueryClient();
  const invalidateBillingRun = useInvalidateClientBillingRun();

  return useMutation({
    mutationFn: (payload: CreateBillingRunPayload) =>
      invoiceApi.createClientBillingRun(entityId, payload),
    onSuccess: (result) => {
      const count = result.invoices.length;
      toast.success(
        count === 1
          ? `Draft invoice ${result.invoices[0]!.invoiceNumber} created`
          : `${count} draft invoices created`,
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.lists() });
      // The run stamps time entries AND expenses as invoiced, so any expense
      // list still on screen is now showing a stale billing state.
      queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all });
      void invalidateBillingRun(entityId);
    },
    onError: (error: unknown) => {
      // A 409 here is the double-billing guard talking; its message names the
      // conflicting invoice, so surface it rather than a generic failure.
      const message =
        error instanceof Error ? error.message : 'Failed to create invoices';
      toast.error(message);
    },
  });
}

/**
 * Bulk delete invoices mutation hook
 */
export function useBulkDeleteInvoicesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => invoiceApi.bulkDeleteInvoices(ids),
    onSuccess: (result) => {
      toast.success(`${result.deletedCount} invoice${result.deletedCount === 1 ? '' : 's'} deleted`);
      // Invalidate invoices list
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.lists() });
    },
    onError: (error) => {
      toast.error('Failed to delete invoices');
      console.error('Bulk delete invoices error:', error);
    },
  });
}
